import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiCalendar, FiUser, FiMessageSquare } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will contact you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      details: '(123) 456-7890',
      subtext: 'Call us anytime'
    },
    {
      icon: FiMail,
      title: 'Email',
      details: 'info@dentalcare.com',
      subtext: 'Send us a message'
    },
    {
      icon: FiMapPin,
      title: 'Address',
      details: '123 Dental Street, Health City, HC 12345',
      subtext: 'Visit our clinic'
    },
    {
      icon: FiClock,
      title: 'Hours',
      details: 'Mon-Fri: 8AM-6PM, Sat: 9AM-4PM',
      subtext: 'Emergency 24/7'
    }
  ];

  const services = [
    'General Dentistry',
    'Cosmetic Dentistry',
    'Orthodontics',
    'Pediatric Dentistry',
    'Oral Surgery',
    'Emergency Care'
  ];

  return (
    <div>
      <Helmet>
        <title>Contact Us - DentalCare | Schedule Your Appointment Today</title>
        <meta name="description" content="Contact DentalCare to schedule your dental appointment. Call (123) 456-7890 or use our online form. Located at 123 Dental Street, Health City. Emergency care available 24/7." />
        <meta name="keywords" content="contact dentist, schedule appointment, dental clinic contact, emergency dentist, dental office hours, book appointment" />
        <link rel="canonical" href="https://dentalcare.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
              Ready to schedule your appointment or have questions about our services? 
              We're here to help you achieve your best smile.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-emerald-50 rounded-lg p-6 text-center hover:bg-emerald-100 transition-colors"
              >
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={info.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-emerald-900 mb-2">
                  {info.title}
                </h3>
                <p className="text-gray-700 font-medium mb-1">{info.details}</p>
                <p className="text-gray-500 text-sm">{info.subtext}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h2 className="text-3xl font-bold text-emerald-900 mb-6">
                Schedule an Appointment
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    <SafeIcon icon={FiUser} className="inline w-5 h-5 mr-2" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    <SafeIcon icon={FiMail} className="inline w-5 h-5 mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    <SafeIcon icon={FiPhone} className="inline w-5 h-5 mr-2" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    <SafeIcon icon={FiCalendar} className="inline w-5 h-5 mr-2" />
                    Service Needed
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                  >
                    <option value="">Select a service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    <SafeIcon icon={FiMessageSquare} className="inline w-5 h-5 mr-2" />
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Tell us about your dental needs or preferred appointment time"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                  <span>Schedule Appointment</span>
                </button>
              </form>
            </motion.div>

            {/* Map & Additional Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Map Placeholder */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="h-64 bg-emerald-100 flex items-center justify-center">
                  <div className="text-center">
                    <SafeIcon icon={FiMapPin} className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-emerald-900 mb-2">
                      Visit Our Clinic
                    </h3>
                    <p className="text-gray-600">
                      123 Dental Street<br />
                      Health City, HC 12345
                    </p>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-emerald-900 text-white rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Emergency Contact</h3>
                <p className="mb-4">
                  For dental emergencies outside of regular hours, please call our 
                  emergency line. We provide 24/7 emergency dental care.
                </p>
                <a
                  href="tel:+1234567890"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  <span>Emergency: (123) 456-7890</span>
                </a>
              </div>

              {/* Office Hours */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-emerald-900 mb-4">
                  Office Hours
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="font-medium">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Saturday</span>
                    <span className="font-medium">9:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sunday</span>
                    <span className="font-medium text-red-600">Closed</span>
                  </div>
                  <div className="border-t pt-2 mt-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Emergency</span>
                      <span className="font-medium text-emerald-600">24/7 Available</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about appointments and our services
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: 'How do I schedule an appointment?',
                answer: 'You can schedule an appointment by calling us, using our online form, or visiting our clinic during business hours.'
              },
              {
                question: 'Do you accept insurance?',
                answer: 'Yes, we accept most major dental insurance plans. Please contact us to verify your specific coverage.'
              },
              {
                question: 'What should I expect during my first visit?',
                answer: 'Your first visit includes a comprehensive examination, X-rays if needed, and a discussion of your treatment plan.'
              },
              {
                question: 'Do you offer emergency dental services?',
                answer: 'Yes, we provide 24/7 emergency dental care. Call our emergency line for urgent dental problems.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-emerald-50 rounded-lg p-6"
              >
                <h3 className="text-lg font-semibold text-emerald-900 mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-700">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;